import { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Alert, Box, Snackbar } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';

import type {
  Tier3TripLegAttestation,
  TripLegAttestationItem,
} from '../../../data/models/trip-leg.model';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { AttestTripsTable } from './AttestTripsTable';
import { DataGridHeader } from '../../tables/DataGridHeader';
import { DataGridTitle } from '../../tables/DataGridTitle';
import { ConfirmAttestationDialog } from './ConfirmAttestationDialog';
import type { PagedList } from '../../../data/models/paged-list.model';
import {
  TripLegStatus,
  tripStatusAttestationListTier3,
} from '../../../data/models/trip-leg-status.model';

import {
  selectLoadingTripLegs,
  selectTripLegError,
  selectTripLegEvent,
  selectTripLegs,
  setLoadingTripLegs,
  setTripLegEvent,
  setTripLegError,
  fetchTripLegsSearchAsync,
} from '../../../data/trip.slice';
import {
  attestTier3TripLegAsync,
  selectAttestationError,
  selectAttestationQueueLength,
  selectSubmittedAttestation,
  setSubmittedAttestation,
  setAttestationError,
  setSubmittingAttestation,
  clearAttestationQueue,
} from '../../../data/provider.slice';
import {
  fetchChargeTypesAsync,
  fetchTimeZonesAsync,
} from '../../../data/reference.slice';
import { fetchSingleApplicationGlobalSettingAsync } from '../../../data/settings.slice';
import {
  setErrorMessage,
  setSuccessMessage,
  clearMessages,
  selectErrorMessage,
} from '../../../data/ui.slice';

import authService from '../../../services/authService';
import userService from '../../../services/userService';
import { webSocketService } from '../../../services/webSocketService';

import { attestationUtils } from '../../../services/attestationUtils';
import { DateTime } from 'luxon';
import { TripLegSearchMenu } from '../../../data/requests/trip-legs';
import { TripLegSearchResponseItem } from '../../../data/models/trip-leg-search.model';

const PREFIX = 'AttestTripsTier3';

const classes = {
  container: `${PREFIX}-container`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(4),
    width: '100%',
  },
}));

const PAYLOAD_DEFAULT: Tier3TripLegAttestation = {
  transportationProviderID: undefined,
  lineItems: [],
};
const PAYLOAD_LINE_ITEM: Tier3TripLegAttestation['lineItems'][number] = {
  tripLegID: undefined, // 313459
  driverName: undefined, // "Jimmy Smith"
};

const AttestTripsTier3 = () => {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const [providerId, setProviderId] = useState<number | null>(null);

  const loading = useAppSelector(selectLoadingTripLegs);
  const errorMessage = useAppSelector(selectErrorMessage);
  const [snackbarErrorMessage, setSnackbarErrorMessage] = useState('');
  const tripLegError = useAppSelector(selectTripLegError);
  const attestations = useAppSelector(selectTripLegs);
  const attestationQueueLength = useAppSelector(selectAttestationQueueLength);
  const [tripLegsForAttestation, setTripLegsForAttestation] = useState<
    TripLegAttestationItem[]
  >([]);
  const [confirmAttestationDialogOpen, setConfirmAttestationDialogOpen] =
    useState(false);
  const [termsHTML, setTermsHTML] = useState<string>('');
  const submittedAttestation = useAppSelector(selectSubmittedAttestation);
  const attestationError = useAppSelector(selectAttestationError);
  const tripLegEvent = useAppSelector(selectTripLegEvent);

  let filteredAttestations: PagedList<TripLegSearchResponseItem> | null = null;
  if (attestations) {
    const sortedItems = [...attestations.items]
      .filter((tl) => {
        const appointmentDate = DateTime.fromISO(tl.appointmentDate);
        const timespanStart = attestationUtils.getStartDate();
        const isTripLegInvoiced = tl.isTripLegInvoiced;
        return appointmentDate >= timespanStart && !isTripLegInvoiced;
      })
      .sort((a, b) => a.appointmentDate.localeCompare(b.appointmentDate));

    filteredAttestations = {
      ...attestations,
      items: sortedItems,
    };
  }

  useEffect(() => {
    return () => {
      dispatch(clearMessages());
      dispatch(setTripLegError(null));
    };
  }, [dispatch]);

  useEffect(() => {
    if (errorMessage) {
      if (errorMessage.indexOf('MagicLink') >= 0) {
        setSnackbarErrorMessage(t('attestation.expiredToken'));
      } else {
        const msg = errorMessage.startsWith('snackbar.')
          ? t(errorMessage)
          : errorMessage;
        setSnackbarErrorMessage(msg);
      }
      dispatch(setErrorMessage(''));
    }
  }, [errorMessage]);

  const fetchTripLegsForAttestation = () => {
    dispatch(setLoadingTripLegs(true));
    const startDate = attestationUtils.getStartDate();
    const endDate = DateTime.local({ zone: 'America/Los_Angeles' }).minus({
      days: 1,
    });

    if (providerId) {
      dispatch(
        fetchTripLegsSearchAsync({
          menu: TripLegSearchMenu.RC_TIER3_UNATTTESTED_TRIPS,
          transportationProviderID: providerId,
          includeTripLegStatusLogs: true,
          page: 1,
          pageSize: 1000,
          pickupDateFrom: startDate.toISODate(),
          pickupDateTo: endDate.toISODate(),
        }),
      );
    }
  };

  useEffect(() => {
    if (providerId) {
      fetchTripLegsForAttestation();
    }
  }, [providerId]);

  useEffect(() => {
    fetch('/invoice-agreement.html').then((resp: any) =>
      resp.text().then((html: string) => {
        setTermsHTML(html);
      }),
    );
    authService.getTier3Tokens().subscribe((tokens) => {
      dispatch(fetchChargeTypesAsync());
      dispatch(fetchTimeZonesAsync());
      dispatch(
        fetchSingleApplicationGlobalSettingAsync({
          applicationSettingBC: 'NO_LOAD_MINIMUM_WAIT_MINUTES',
          applicationTitleBC: 'F1',
          sortModel: null,
        }),
      );

      webSocketService.setSessionInfo(
        {
          accessToken: tokens.accessToken,
          accessTokenID: tokens.idToken,
        },
        true,
      );

      userService.getUserInfo().subscribe({
        next: (userInfo) => {
          setProviderId(userInfo.transportationProviderID);
          if (userInfo?.userAccountID) {
            webSocketService.setSessionInfo({
              userAccountID: userInfo.userAccountID,
              loggedInTransportationProviderID:
                userInfo.transportationProviderID,
            });
          }
          webSocketService.connect(dispatch);
        },
        error: (err) => {
          // eslint-disable-next-line no-console
          console.warn('error: ', err);
        },
      });
    });
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      return t('attestation.confirmLeave');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [t]);

  const handleTripsUpdated = (tripLegs: TripLegSearchResponseItem[]) => {
    setTripLegsForAttestation(tripLegs);
  };

  const buildAttestations = (): Tier3TripLegAttestation | null => {
    const payload = { ...PAYLOAD_DEFAULT };
    payload.transportationProviderID =
      tripLegsForAttestation[0]?.transportationProviderID ?? undefined;
    if (!payload.transportationProviderID) {
      return null;
    }
    payload.lineItems = tripLegsForAttestation.map((tripLeg) => {
      const lineItem = { ...PAYLOAD_LINE_ITEM };
      lineItem.tripLegID = tripLeg.tripLegID;
      lineItem.driverName = tripLeg.driverName;
      return lineItem;
    });
    return payload;
  };

  const onTableSubmit = () => {
    setConfirmAttestationDialogOpen(true);
  };

  const onDialogSubmit = () => {
    const builtAttestations = buildAttestations();
    if (builtAttestations) {
      dispatch(attestTier3TripLegAsync(builtAttestations));
    }
  };

  useEffect(() => {
    if (submittedAttestation || attestationError) {
      setConfirmAttestationDialogOpen(false);
      dispatch(setSubmittingAttestation(false));
      dispatch(setSubmittedAttestation(false));
      dispatch(clearAttestationQueue());
      fetchTripLegsForAttestation();
    }
    if (attestationError) {
      dispatch(setErrorMessage(attestationError));
      dispatch(setAttestationError(null));
    }
    if (submittedAttestation) {
      dispatch(setSuccessMessage(t('attestation.invoiceGenerated')));
    }
  }, [submittedAttestation, attestationError]);

  useEffect(() => {
    if (tripLegEvent) {
      if (
        tripLegEvent === TripLegStatus.Accepted ||
        tripLegEvent === TripLegStatus.Onsite ||
        tripLegEvent === TripLegStatus.Onboard ||
        tripLegEvent === TripLegStatus.Completed ||
        tripLegEvent === TripLegStatus.CanceledLate ||
        tripLegEvent === TripLegStatus.NoLoad
      ) {
        fetchTripLegsForAttestation();
      }
      dispatch(setTripLegEvent(null));
    }
  }, [tripLegEvent]);

  useEffect(() => {
    if (tripLegError) {
      dispatch(setErrorMessage(tripLegError));
      dispatch(setTripLegError(null));
    }
  }, [tripLegError, dispatch]);

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <StyledBox className={classes.container}>
        <DataGridHeader>
          <DataGridTitle>{t('trips.attestAndInvoice')}</DataGridTitle>
        </DataGridHeader>
        <AttestTripsTable
          attestableTripLegs={filteredAttestations}
          onTripsUpdated={handleTripsUpdated}
          onSubmit={onTableSubmit}
          providerId={providerId}
          loading={loading}
        />
        {confirmAttestationDialogOpen && (
          <ConfirmAttestationDialog
            open={confirmAttestationDialogOpen}
            termsContent={termsHTML}
            attestationQueueLength={attestationQueueLength}
            attestationQueueTotal={tripLegsForAttestation?.length}
            close={() => setConfirmAttestationDialogOpen(false)}
            onSubmit={onDialogSubmit}
          />
        )}
        <Snackbar
          open={!!snackbarErrorMessage}
          onClose={() => setSnackbarErrorMessage('')}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert variant="filled" severity="error">
            {snackbarErrorMessage}
          </Alert>
        </Snackbar>
      </StyledBox>
    </LocalizationProvider>
  );
};

export { AttestTripsTier3 };
